import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import {
  filter,
  find,
  first,
  get,
  isEmpty,
  map,
  memoize,
  pick,
  isArray,
  concat
} from 'lodash';
import { DateTime } from 'luxon';

import { evaluateConditionals } from '@broadleaf/admin-components/dist/common/services/ConditionalHelper';
import enableOverrides from '@broadleaf/admin-components/dist/common/helpers/enableOverrides';
import SubmissionError from '@broadleaf/admin-components/dist/common/components/SubmissionError';
import { buildHint } from '@broadleaf/admin-components/dist/common/components/Hint';
import { buildHelpText } from '@broadleaf/admin-components/dist/common/components/HelpText';
import { Button, SVG } from '@broadleaf/admin-components/dist/common/index';
import ActionListGridAdvancedSearch from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/components/ActionListGridAdvancedSearch';
import ListGrid from '@broadleaf/admin-components/dist/collection/components/ListGrid';
import FilterType from '@broadleaf/admin-components/dist/collection/constants/FilterType';
import {
  getAttribute,
  getEndpointByType
} from '@broadleaf/admin-components/dist/metadata/utils/MetadataUtils';
import { getFilterPredicate } from '@broadleaf/admin-components/dist/collection/utils/filters';
import { apply } from '@broadleaf/admin-components/dist/common/utils/lodash';
import renderOrUseDefault from '@broadleaf/admin-components/dist/common/utils/delegateRenderProp/renderOrUseDefault';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import useTracking from '@broadleaf/admin-components/dist/tracking/hooks/useTracking';
import SearchSettingsCache from '@broadleaf/admin-components/dist/tracking/caches/searchSettings';

import ActionListGridFacets from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/components/ActionListGridFacets';
import ActionListGridFacetHeader from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/components/ActionListGridFacetHeader';
import ActionListGridFacetMessages from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/components/ActionListGridFacetMessages';

import TileGrid from '@broadleaf/admin-components/dist/collection/components/TileGrid';

import type { HintProps } from '@broadleaf/admin-components/dist/common/components/Hint';
import type { HelpTextProps } from '@broadleaf/admin-components/dist/common/components/HelpText';
import type { ActionListGridActionProps } from '@broadleaf/admin-components/dist/types/collection';
import type {
  ICommonGridHeader,
  ICommonSelectableTypeIS
} from '@broadleaf/admin-components/dist/types/common';
import type {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';

import messages from '@broadleaf/admin-components/dist/collection/components/ActionListGrid/ActionListGrid.messages';
import GridActionGroupDropdown from './components/GridActionGroupDropdown';

const logger = log.getLogger('collection.components.ActionListGrid');

/**
 * Override framework version to:
 *  - prevent updating the URL when using a query filter on an external grid.
 *  - group the grid actions in a div with styles for wrapping to new lines
 * Customizations marked with `// Aurora customization`
 *
 * Renders a ListGrid with a set of actions in various placements.
 */
export const AuroraActionListGrid: FC<ActionListGridProps> = props => {
  const {
    actionDefinitions,
    className,
    wrapperClassName,
    customCssClasses = [],
    rowMessages,
    headers,
    hint,
    helpText,
    renderColumn,
    renderRowMessage,
    rowActionHeaderLabel,
    tiled = false,
    useFacetedSearch,
    ...listGridProps
  } = props;
  const {
    searchSettings: { searchEnabled }
  } = useTracking();
  const isFacetedSearchEnabled = useFacetedSearch && searchEnabled;
  const { actionProps } = listGridProps;

  const hasRowActions = !!find(actionDefinitions, { placement: 'ROW' });
  const rowActionAwareHeaders = useMemo(() => {
    return hasRowActions
      ? [...headers, createRowActionsHeader(rowActionHeaderLabel)]
      : headers;
  }, [hasRowActions, headers, rowActionHeaderLabel]);
  const hasRowMessages = !isEmpty(rowMessages);
  const rowMessageAwareHeaders = useMemo(() => {
    return hasRowMessages
      ? [...rowActionAwareHeaders, createRowMessagesHeader('', rowMessages)]
      : rowActionAwareHeaders;
  }, [hasRowMessages, rowActionAwareHeaders]);
  const header = renderHeader({
    ...props,
    isFacetedSearchEnabled
  });

  const [filtersCollapsed, setFiltersCollapsed] = useState(
    !!actionProps?.metadata?.attributes?.collapseFiltersByDefault
  );

  const footerButton = renderOrUseDefault(
    props.renderFooterButton,
    props,
    null
  );

  return (
    <div className={cx('tw-flex tw-justify-between', wrapperClassName)}>
      <div
        className={cx(
          'ActionListGrid form-group tw-flex tw-w-full tw-flex-col',
          className,
          customCssClasses
        )}
      >
        {header}
        {!tiled && (
          <ListGrid
            headers={rowMessageAwareHeaders}
            renderColumn={columnData => {
              if (headers.length === columnData.headerIndex) {
                // render row messages and row actions in last columns
                if (columnData.header.type === 'MESSAGES') {
                  return (
                    <div className="RowMessages tw-flex tw-flex-row tw-items-center tw-justify-center tw-px-2">
                      {renderRowMessages(props, columnData)}
                    </div>
                  );
                } else {
                  return (
                    <div className="RowActions tw-absolute tw-inset-0 tw-z-50 tw-hidden tw-flex-row tw-items-center tw-justify-end tw-px-2 group-hover:tw-flex">
                      {renderRowActions(props, columnData)}
                    </div>
                  );
                }
              }

              return renderColumn
                ? renderColumn(columnData)
                : get(columnData.row, columnData.header.name, '');
            }}
            {...listGridProps}
          />
        )}
        {tiled && (
          <TileGrid
            headers={rowMessageAwareHeaders}
            renderContent={columnData => {
              if (headers.length === columnData.headerIndex) {
                // render row messages and row actions in last columns
                if (columnData.header.type === 'MESSAGES') {
                  return (
                    <div className="RowMessages tw-flex tw-flex-row tw-items-center tw-justify-center tw-px-2">
                      {renderRowMessages(props, columnData)}
                    </div>
                  );
                } else {
                  return (
                    <div className="RowActions tw-absolute tw-right-0 tw-top-0 tw-z-50 tw-hidden tw-flex-row tw-items-center tw-justify-end tw-px-2 group-hover:tw-flex">
                      {renderRowActions(props, columnData)}
                    </div>
                  );
                }
              }
              return renderColumn
                ? renderColumn(columnData)
                : get(columnData.row, columnData.header.name, '');
            }}
            {...listGridProps}
          />
        )}
        {footerButton}
        {!!hint && buildHint(hint)}
      </div>
      {isFacetedSearchEnabled && (
        <ActionListGridFacets
          actionProps={actionProps}
          collapsed={filtersCollapsed}
          setCollapsed={setFiltersCollapsed}
        />
      )}
    </div>
  );
};

function renderHeader(
  props: ActionListGridProps & { isFacetedSearchEnabled: boolean }
) {
  const { useFacetedSearch, isFacetedSearchEnabled } = props;
  const labelElement = renderOrUseDefault(
    props.renderDecorations,
    props,
    renderDefaultLabel
  );
  const errorElement = !isEmpty(props.error) && (
    <SubmissionError
      errors={isArray(props.error) ? props.error : [props.error]}
      label="Errors"
    />
  );
  const inlineFilterElements = renderInlineFilterActions(props);
  const queryFilterElement = renderQueryAction(props);
  const advancedFilterElements = renderAdvancedFilterActions(props);
  const gridActionElements = renderGridActions(props);
  const refreshButton = renderRefreshButton(props.actionProps);
  const activeFiltersElements = renderActiveFilters(props);
  if (
    !labelElement &&
    !errorElement &&
    !inlineFilterElements &&
    !queryFilterElement &&
    !advancedFilterElements &&
    !gridActionElements &&
    !activeFiltersElements
  ) {
    // render nothing for the header if there is no content
    return null;
  }

  if (isFacetedSearchEnabled) {
    return (
      <ActionListGridFacetHeader
        actionProps={props.actionProps}
        errorElement={errorElement}
        gridActionElements={gridActionElements}
        refreshButton={refreshButton}
        labelElement={labelElement}
        queryFilterElement={queryFilterElement}
      />
    );
  }

  const key = `toggle-facets.${props.augmentationKey}`;
  const isFacetedSearchOn = SearchSettingsCache.get(key);

  return (
    <>
      <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
        {labelElement}
        <div
          className={cx('tw-flex tw-items-center tw-justify-end', {
            'flex-grow-1': !labelElement
          })}
        >
          {inlineFilterElements}
          {useFacetedSearch && (
            <ActionListGridFacetMessages
              disabled={!isFacetedSearchOn}
              augmentationKey={props.augmentationKey}
            />
          )}
          {errorElement}
          {queryFilterElement}
          {advancedFilterElements}
          {/* Aurora Customization */}
          {gridActionElements && (
            <div
              className={
                'tw-ml-2 tw-flex tw-items-center tw-justify-end tw-gap-2'
              }
            >
              {gridActionElements}
            </div>
          )}
          {/* End Aurora Customization */}
          {refreshButton}
        </div>
      </div>
      {activeFiltersElements}
    </>
  );
}

const renderDefaultLabel = ({ label, helpText, required }) => {
  if (!label) return null;

  return (
    <label className="control-label tw-flex tw-items-center tw-space-x-2 tw-self-end rtl:tw-space-x-reverse">
      {label &&
        (required ? (
          <span className="required">{label}</span>
        ) : (
          <span>{label}</span>
        ))}
      {!!helpText && buildHelpText(helpText)}
    </label>
  );
};

/**
 * Renders the refresh button for this list grid
 *
 * @param {Array.<Object>} actionProps.metadata the metadata props
 * @param {Function} actionProps.handleRead the handle read action
 */
function renderRefreshButton(actionProps) {
  if (!actionProps) {
    return null;
  }
  const { metadata, handleRead } = actionProps;
  if (!get(metadata?.attributes, 'refreshButton')) {
    return null;
  }
  return (
    <Button
      className="tw-relative tw-ml-2 tw-flex tw-items-center tw-justify-center"
      type="button"
      size="sm"
      onClick={handleRead}
    >
      <SVG
        name="refresh"
        viewBox="0 0 20 20"
        fill="currentColor"
        strokeWidth={1.5}
        className="tw-w-4 tw-align-middle"
      />
    </Button>
  );
}

/**
 * Resolves the effective grouping type for an action definition, checking attribute overrides.
 */
export function getEffectiveType(actionDefinition: any): string {
  const groupAttr = getAttribute(actionDefinition, 'group');
  const typeAttr = getAttribute(actionDefinition, 'type');
  return groupAttr || typeAttr || actionDefinition?.type || '';
}

/**
 * Determines if an action definition should render directly as a primary CTA button.
 */
export function isPrimaryAction(actionDefinition: any): boolean {
  const isPrimaryAttr = getAttribute(actionDefinition, 'isPrimary');
  if (isPrimaryAttr === 'true' || isPrimaryAttr === true) {
    return true;
  }
  const effectiveType = (
    getEffectiveType(actionDefinition) || ''
  ).toUpperCase();
  return effectiveType === 'CREATE' || effectiveType === 'ADD';
}

/**
 * Renders a single grid action component.
 */
function renderSingleAction(
  actionDefinition: any,
  index: string | number,
  props: any
) {
  const { actionComponents, actionProps, renderGridAction } = props;
  const predicate = pick(actionDefinition, ['placement', 'type']);
  const actionInformation = find(actionComponents, predicate);

  if (!actionInformation) {
    if (renderGridAction) {
      return renderGridAction(
        actionDefinition,
        apply(actionProps, actionDefinition)
      );
    } else {
      logger.debug(
        'Received a grid action definition without a known registered action to render for it. Be sure you are using `ActionRegistrar#register` to register action components for your top-level metadata component.\n',
        '\n' + JSON.stringify(actionDefinition, null, 2)
      );
      return null;
    }
  }

  const { component: GridActionComponent } = actionInformation;

  return (
    <GridActionComponent
      key={index}
      actionDefinition={actionDefinition}
      {...getFilterActionProps(actionDefinition, props)}
      {...getInheritedActionProps(props)}
      {...apply(actionProps, actionDefinition)}
    />
  );
}

/**
 * Renders the grid actions for this list grid. This will prioritize `actionComponents`
 * over the `renderGridAction` render function if a component exists.
 *
 * Primary actions (CREATE, ADD, or isPrimary) render directly as standalone CTA buttons.
 * Secondary actions with matching effective types group into type-based dropdowns when >= 2 items.
 * Single non-primary actions render directly as standalone buttons.
 */
function renderGridActions(props) {
  const { actionDefinitions, actionProps } = props;
  const gridActionDefinitions = filter(actionDefinitions, {
    placement: 'GRID'
  });

  if (isEmpty(gridActionDefinitions)) {
    return null;
  }

  const visibleGridActions = filter(gridActionDefinitions, actionDefinition => {
    if (actionProps && (actionProps.contextParams || actionProps.state)) {
      return checkShouldRenderGridAction({
        actionDefinition,
        state: actionProps.state ?? actionProps.contextParams
      });
    }
    return true;
  });

  if (isEmpty(visibleGridActions)) {
    return null;
  }

  const primaryActions: any[] = [];
  const groupedByEffectiveType: Record<string, any[]> = {};

  visibleGridActions.forEach(actionDefinition => {
    if (isPrimaryAction(actionDefinition)) {
      primaryActions.push(actionDefinition);
    } else {
      const effectiveType = getEffectiveType(actionDefinition) || 'OTHER';
      if (!groupedByEffectiveType[effectiveType]) {
        groupedByEffectiveType[effectiveType] = [];
      }
      groupedByEffectiveType[effectiveType].push(actionDefinition);
    }
  });

  const renderedElements: React.ReactNode[] = [];

  primaryActions.forEach((actionDefinition, index) => {
    const element = renderSingleAction(
      actionDefinition,
      `primary-${index}`,
      props
    );
    if (element) {
      renderedElements.push(element);
    }
  });

  Object.entries(groupedByEffectiveType).forEach(([type, actions]) => {
    if (actions.length === 1) {
      const element = renderSingleAction(actions[0], `single-${type}-0`, props);
      if (element) {
        renderedElements.push(element);
      }
    } else if (actions.length >= 2) {
      renderedElements.push(
        <GridActionGroupDropdown
          key={`group-dropdown-${type}`}
          groupType={type}
          actionDefinitions={actions}
          gridProps={props}
          renderSingleAction={renderSingleAction}
        />
      );
    }
  });

  if (isEmpty(renderedElements)) {
    return null;
  }

  return renderedElements;
}

/**
 * Renders the QUERY_FILTER action for this list grid. This will only render a single action,
 * the action with placement of `QUERY_FILTER`, if it exists.
 *
 * @see #renderInlineFilterActions for where the `INLINE_FILTER` are rendered
 * @see #renderAdvancedFilterActions for where the `ADVANCED_FILTER` are rendered
 * @param  {Array.<Object>} props.actionComponents the action components
 * @param  {Array.<Object>} props.actionDefinitions the action definitions
 * @param  {Object|Function} props.actionProps props to pass to the action
 * @return {Array.<Element>} the rendered content
 */
function renderQueryAction(props) {
  const { actionComponents, actionDefinitions, actionProps } = props;
  const queryActionDefinition = find(actionDefinitions, {
    placement: 'QUERY_FILTER'
  });
  if (!queryActionDefinition) {
    // if no QUERY_FILTER action definition found, we don't render anything
    return null;
  }

  const predicate = pick(queryActionDefinition, ['placement', 'type']);
  const actionInformation = find(actionComponents, predicate);
  if (!actionInformation) {
    logger.debug(
      'Received a QUERY_FILTER action definition without a known registered action to render for it. Be sure you are using `ActionRegistrar#register` to register action components for your top-level metadata component.\n',
      '\n' + JSON.stringify(queryActionDefinition, null, 2)
    );
    return null;
  }

  const { component: QueryActionComponent } = actionInformation;
  return (
    <QueryActionComponent
      actionDefinition={queryActionDefinition}
      {...getFilterActionProps(queryActionDefinition, props)}
      {...getInheritedActionProps(props)}
      {...apply(actionProps, queryActionDefinition)}
    />
  );
}

/**
 * Renders the INLINE_FILTER actions for this list grid. This will render zero
 * or more actions with placement of `INLINE_FILTER`.
 *
 * @see #renderAdvancedFilterActions for where the `ADVANCED_FILTER` are rendered
 * @param  {Array.<Object>} props.actionComponents the action components
 * @param  {Array.<Object>} props.actionDefinitions the action definitions
 * @param  {Object|Function} props.actionProps props to pass to each action
 * @return {Array.<Element>} the rendered content
 */
function renderInlineFilterActions(props) {
  const { actionComponents, actionDefinitions, actionProps } = props;
  const inlineFilterActionDefinitions = filter(actionDefinitions, {
    placement: 'INLINE_FILTER'
  });

  if (isEmpty(inlineFilterActionDefinitions)) {
    return null;
  }

  return (
    <div className="tw-flex tw-flex-grow tw-items-center tw-justify-end">
      {map(inlineFilterActionDefinitions, (actionDefinition, index) => {
        const predicate = pick(actionDefinition, ['placement', 'type']);
        const actionInformation = find(actionComponents, predicate);

        if (!actionInformation) {
          logger.debug(
            'Received a INLINE_FILTER action definition without a known registered action to render for it. Be sure you are using `ActionRegistrar#register` to register action components for your top-level metadata component.\n',
            '\n' + JSON.stringify(actionDefinition, null, 2)
          );
          return null;
        }

        const { component: InlineFilterActionComponent } = actionInformation;
        return (
          <InlineFilterActionComponent
            key={index}
            actionDefinition={actionDefinition}
            {...getFilterActionProps(actionDefinition, props)}
            {...getInheritedActionProps(props)}
            {...apply(actionProps, actionDefinition)}
          />
        );
      })}
    </div>
  );
}

/**
 * Renders the ADVANCED_FILTER actions for this list grid. This will render zero
 * or more actions with placement of `ADVANCED_FILTER`.
 *
 * @param  {Array.<Object>} props.actionComponents the action components
 * @param  {Array.<Object>} props.actionDefinitions the action definitions
 * @param  {Object|Function} props.actionProps props to pass to each action
 * @return {Array.<Element>} the rendered content
 */
function renderAdvancedFilterActions(props) {
  const advancedFilterActions = filter(props.actionDefinitions, {
    placement: 'ADVANCED_FILTER'
  });
  if (isEmpty(advancedFilterActions)) {
    return null;
  }
  const advancedFilterComponents = filter(props.actionComponents, {
    placement: 'ADVANCED_FILTER'
  });
  if (isEmpty(advancedFilterComponents)) {
    return null;
  }
  return (
    <ActionListGridAdvancedSearch
      actionComponents={advancedFilterComponents}
      actionDefinitions={advancedFilterActions}
      actionProps={props.actionProps}
      additionalActionProps={getInheritedActionProps(props)}
      filters={filter(props.filters)}
      onFiltersChange={props.onFiltersChange}
    />
  );
}

function renderActiveFilters(props) {
  const { filters } = props;
  // if there are no filters, or none of them are query builder filters, we don't want to render the active filters header.
  // Other filter types ('QUERY' and 'LOOKUP') don't make sense to render here.
  if (
    isEmpty(filters) ||
    !filters.some(group => group.type === FilterType.QUERY_BUILDER)
  ) {
    return null;
  }

  return (
    <div className="tw-flex tw-space-x-2 tw-border tw-bg-gray-100 tw-px-4 tw-py-2">
      <div className="tw-flex tw-items-center tw-text-xs tw-font-semibold">
        <FormattedMessage {...messages.activeFilters} />
      </div>
      {filters
        .filter(group => group.type === FilterType.QUERY_BUILDER)
        .map((group, n) => {
          let connective = '';
          if (n > 0) {
            const prev = filters[n - 1].connective as object;
            if (typeof prev === 'object') {
              connective = prev['labelKey'];
            } else {
              connective = prev as string;
            }
          }

          return (
            <QueryGroup
              key={n}
              group={group}
              connective={connective}
              showParentheses={filters.length > 1}
            />
          );
        })}
    </div>
  );
}

const QueryGroup: FC<any> = ({ group, connective, showParentheses }) => {
  const needsParentheses = group.queryNodes?.length > 1;
  return (
    <div className="tw-flex tw-items-center tw-space-x-2 tw-text-xs">
      {connective && <div className="tw-lowercase">{connective}</div>}
      {showParentheses && needsParentheses && (
        <div className="tw-text-lg">(</div>
      )}

      {group.queryNodes
        ?.filter(qn => qn.isComplete())
        ?.map((qn, i) => {
          return (
            <QueryNode
              key={qn.id}
              queryNode={qn}
              showConnective={
                i < group.queryNodes.length - 1 && group.queryNodes.length > 1
              }
            />
          );
        })}
      {showParentheses && needsParentheses && (
        <div className="tw-text-lg">)</div>
      )}
    </div>
  );
};

const QueryNode: FC<any> = ({ queryNode, showConnective }) => {
  const tracking = useTracking(queryNode.field);
  const [value, setValue] = useState(queryNode.value);

  useEffect(() => {
    if (queryNode.field.type === 'LOOKUP') {
      const hydrateConfig = getEndpointByType(queryNode.field, 'HYDRATE');
      const readOptionForValue = val =>
        request(hydrateConfig, {
          tracking,
          id: val,
          value: val
        })
          .then(response => response.data)
          .then(data => data.name);

      if (Array.isArray(queryNode.value)) {
        Promise.all(map(queryNode.value, readOptionForValue)).then(setValue);
      } else {
        readOptionForValue(queryNode.value).then(setValue);
      }
    } else {
      setValue(queryNode.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryNode]);

  let displayValue = value;
  if (queryNode.field.type === 'BOOLEAN') {
    displayValue = value ? 'Yes' : 'No';
  }

  if (queryNode.field.type === 'MONEY') {
    displayValue = value ? value.amount : '0';
  }

  if (queryNode.field.type === 'DATE') {
    if (isArray(value)) {
      displayValue = map(value, v =>
        DateTime.fromISO(v).toLocaleString(DateTime.DATETIME_MED)
      );
    } else {
      // format into correct timezone and more readable format
      const isoDateTime = DateTime.fromISO(value);
      displayValue = isoDateTime.toLocaleString(DateTime.DATETIME_MED);
    }
  }

  return (
    <div className="tw-flex tw-items-center tw-space-x-2">
      <div className="tw-flex tw-items-center tw-space-x-1 tw-rounded-md tw-bg-gray-300 tw-px-3 tw-py-1">
        <span className="tw-font-bold">{queryNode.field.label}</span>
        <span>{queryNode.operator.label.props.defaultMessage}</span>
        {queryNode.isComplete() && (
          <>
            {isArray(displayValue) &&
              displayValue.map((v, i) => (
                <Fragment key={i}>
                  {i > 0 && <span className="tw-mx-1">,</span>}
                  <span className="tw-flex tw-items-center">
                    {'"'}
                    <span className="tw-font-bold">{v}</span>
                    {'"'}
                  </span>
                </Fragment>
              ))}
            {!isArray(displayValue) && (
              <span className="tw-flex tw-items-center">
                {'"'}
                <span className="tw-font-bold">{displayValue}</span>
                {'"'}
              </span>
            )}
          </>
        )}
      </div>

      {showConnective && (
        <div className="tw-lowercase">{queryNode.connective.labelKey}</div>
      )}
    </div>
  );
};

/**
 * Renders the messages for the row items.
 */
function renderRowMessages(props, columnData) {
  const { rowMessages, renderRowMessage } = props;
  if (isEmpty(rowMessages)) {
    return null;
  }
  return map(rowMessages, rowMessage => {
    if (renderRowMessage) {
      return renderRowMessage({
        metadata: rowMessage,
        className: '!tw-w-auto',
        row: columnData.row,
        key: concat(
          'ListGridCell--',
          columnData.rowIndex,
          columnData.headerIndex
        )
      });
    }
  });
}

/**
 * Renders the row actions for this list grid row. This will prioritize `actionComponents`
 * over the `renderRowAction` render function if a component exists.
 *
 * @param  {Array.<Object>} props.actionComponents the action components
 * @param  {Array.<Object>} props.actionDefinitions the action definitions
 * @param  {Object|Function} props.actionProps props to pass to each action
 * @param  {Function} props.renderRowAction the row action render function
 * @param  {Object} columnData the current column data, including the row
 * @return {Array.<Element>} the rendered content
 */
function renderRowActions(props, columnData) {
  const { actionComponents, actionDefinitions, actionProps, renderRowAction } =
    props;
  const rowActionDefinitions = filter(actionDefinitions, actionDefinition => {
    return (
      actionDefinition.placement === 'ROW' &&
      checkShouldRender({ actionDefinition, columnData })
    );
  });
  if (isEmpty(rowActionDefinitions)) {
    return null;
  }
  return map(rowActionDefinitions, (actionDefinition, index) => {
    const predicate = pick(actionDefinition, ['placement', 'type']);
    const actionInformation = find(actionComponents, predicate);

    if (!actionInformation) {
      if (renderRowAction) {
        // TODO: remove the #renderRowAction option once all usages of `ActionListGrid` have been refactored.
        return renderRowAction(
          actionDefinition,
          columnData,
          apply(actionProps, actionDefinition)
        );
      } else {
        logger.debug(
          'Received a row action definition without a known registered action to render for it. Be sure you are using `ActionRegistrar#register` to register action components for your top-level metadata component.\n',
          '\n' + JSON.stringify(actionDefinition, null, 2)
        );
        return null;
      }
    }

    const { component: RowActionComponent } = actionInformation;
    return (
      <RowActionComponent
        key={index}
        actionDefinition={actionDefinition}
        {...columnData}
        {...getInheritedActionProps(props)}
        {...apply(actionProps, actionDefinition)}
      />
    );
  });
}

function checkShouldRender({ actionDefinition, columnData }): boolean {
  const conditionals = getAttribute(actionDefinition, 'conditionals', []);
  return evaluateConditionals(conditionals, columnData.row);
}

function checkShouldRenderGridAction({ actionDefinition, state }): boolean {
  const conditionals = getAttribute(actionDefinition, 'conditionals', []);
  return evaluateConditionals(conditionals, state);
}

/**
 * <p>
 * Used for getting the props that are inherited by every action rendered in this grid.
 *
 * @return {Object} the inherited action props
 */
export function getInheritedActionProps(props) {
  return pick(props, [
    'actionComponents',
    'actionDefinitions',
    'disabled',
    'headers',
    'idKey',
    'isSelectAll',
    'isSelectVisible',
    'isSelectNonVisible',
    'loading',
    'rowKey',
    'orderable',
    'selectable',
    'selectableType',
    'selectedCount',
    'selectedIds'
  ]);
}

/**
 * Helper function used for getting the props to pass to a filter action component.
 *
 * @param  {Object} actionDefinition         the action definition
 * @param  {Object} props                    the props for the grid
 * @param  {Array.<Object>} props.filters    the set of filters for this grid
 * @param  {Function} props.onFilterChange   the single filter change handler
 * @param  {Function} props.onFiltersChange  the multi filter change handler
 * @return {{ filter: Object, filters: Array.<Object>, onFilterChange: Function, onFiltersChange: Function }}
 */
export function getFilterActionProps(
  actionDefinition,
  { filters, onFilterChange, onFiltersChange }
) {
  const filterPredicate = getFilterPredicate(actionDefinition);
  const actionFilters = filter(filters, filterPredicate);
  return {
    filter: first(actionFilters),
    filters: actionFilters,
    onFilterChange: nextFilter => {
      // Aurora customization
      // Ultimately calls useCollectionState.handleFilterChange
      // Pass updateUrl=false so the URL does not update when query filtering on an external grid.
      if (get(actionDefinition, 'attributes.EXTERNAL_GRID_FILTER')) {
        return onFilterChange(actionDefinition, nextFilter, false);
      }
      // End Aurora customization
      return onFilterChange(actionDefinition, nextFilter);
    },
    onFiltersChange: nextFilters =>
      onFiltersChange(actionDefinition, nextFilters)
  };
}

/**
 * Memoized function for creating the row actions header placeholder.
 *
 * @type {Object}
 */
const createRowActionsHeader = memoize(
  (rowActionHeaderLabel): ICommonGridHeader => ({
    // place-holder for actions
    attributes: {
      // Want to set `sortable` flag to false as this is not data that needs to be sortable.
      sortable: false
    },
    classifier: 'Field',
    label: rowActionHeaderLabel,
    name: '',
    type: 'ROW_ACTIONS'
  })
);
/**
 * Memoized function for creating the row messages header placeholder.
 *
 * @type {Object}
 */
const createRowMessagesHeader = memoize(
  (rowActionHeaderLabel, rowMessages): ICommonGridHeader => ({
    // place-holder for actions
    attributes: {
      // Want to set `sortable` flag to false as this is not data that needs to be sortable.
      sortable: false
    },
    key: 'column:row-messages',
    classifier: 'Field',
    label: rowActionHeaderLabel,
    name: '',
    type: 'MESSAGES',
    message: rowMessages
  })
);

export interface ActionListGridProps {
  /**
   * Array of action information with components. Used for providing
   * the components that should be rendered for action definitions.
   *
   * @see #renderRowAction or #renderGridAction for an alternative route
   */
  actionComponents?: Array<object>;
  /**
   * Array of action definitions for this list grid. This might contain action
   * definitions with various placement such as `GRID` or `ROW` actions.
   *
   * @see #actionComponents for the prop used to drive the rendering of the actions
   * @see #actionProps for the prop that allows additional props to be passed to each action
   */
  actionDefinitions?: Array<IMetadataAction>;

  /**
   * Additional props to pass to each action component. This allows the parent of
   * the {@link ActionListGrid} to pass down important props to actions without
   * requiring the usage of the Context API.
   */
  actionProps?: Partial<ActionListGridActionProps>;

  augmentationKey: string;

  /**
   * Custom user defined class names to be applied to the list grid.
   */
  customCssClasses?: string[];

  /** Whether or not this list grid is in a disabled state. */
  disabled?: boolean;

  /**
   * This disables drag-and-drop behavior for Sorted entities where
   * all rows have sorted !== true.
   * A ListGrid action must be used if none of the rows are sorted yet.
   * See ExternalGridCollection#renderUpdateOrderAction for example.
   * Drag-and-drop also requires orderable === true.
   */
  disableDragIfAllUnsorted?: boolean;

  /** an error passed from the parent, most likely due to an invalid action */
  error?: Array<unknown> | string;

  /**
   * Optional message that is rendered when zero rows are provided. Can be a
   * simple string or a {@link FormattedMessage} to support localization.
   */
  emptyMessage?: string | ReactNode | Element;

  /**
   * The set of filters for this grid. These will be selected from and passed to
   * various filter action components to provide them with their initial values.
   *
   * @see `onFilterChange` for the handler to manage a single filter
   * @see `onFiltersChange` for the handler to manage a multi filter
   */
  filters?: Array<{
    /** The name of the filter, usually derived from the action definition's 'filterKey' attribute.  */
    name: string;
    /** The type of the filter, usually derived from the action type. */
    type: string;
  }>;

  /** the column headers for this list grid */
  headers?: Array<ICommonGridHeader>;
  /** Either a [HelpText](#helpText) object or just the message string */
  helpText?: string | HelpTextProps;
  /** Either a [Hint](#hint) object or just the message string */
  hint?: string | HintProps;
  /** the label for this action list grid */
  label?: string | ReactNode;
  /** whether or not the ListGrid should render a loading state */
  loading?: boolean;

  /**
   * Optional. If orderable is true, this is the responder called before a drag
   * action starts.
   * It is called before any snapshot values are updated.
   *
   * @see https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/responders.md#secondary-onbeforedragstart
   *
   * @param start Information about the row getting dragged
   */
  onDragBeforeStart?: Function;
  /**
   * Required if orderable is true, this is the responder called when a drag
   * action has ended.
   * It is the responsibility of this responder to synchronously apply
   * changes that have resulted from the drag.
   *
   * @see https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/responders.md#ondragend-required
   *
   * @param result Information about the row that was dragged, its destination and the reason why the drag ended
   * @param provided Provides an 'announce' function to synchronously announce a message to screen readers
   * @param rows List of row data used for the grid
   */
  onDragEnd?: Function;
  /**
   * Optional. If orderable is true, this is the responder called when a drag
   * action starts.
   *
   * @see https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/responders.md#ondragstart-optional
   *
   * @param start Information about the row getting dragged
   * @param provided Provides an 'announce' function to synchronously announce a message to screen readers
   */
  onDragStart?: Function;
  /**
   * Optional. If orderable is true, this is the responder called when
   * something has changed during a drag.
   * Called when:
   * - The position of the Draggable has changed
   * - The Draggable is now over a different Droppable
   * - The Draggable is now over no Droppable
   *
   * @see https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/responders.md#ondragstart-optional
   *
   * @param update Information about the row getting dragged and its destination
   * @param provided Provides an 'announce' function to synchronously announce a message to screen readers
   */
  onDragUpdate?: Function;

  /**
   * Change handler called when a single filter is added, removed, or updated.
   * This receives two parameters, the action definition and the filter object.
   *
   * @see `onFiltersChange` for the handler that is used for multi-filter actions.
   *
   * @param {ActionPropShape} actionDefinition the action definition of the filter that changed
   * @param {Object} filter the next filter object, or null if removed
   */
  onFilterChange?: Function;

  /**
   * Change handler called when a multi-filter is changed.
   * This receives two parameters, the action definition and an array of filter objects.
   *
   * @see `onFilterChange` for the handler that is used for single-filter actions.
   *
   * @param {ActionPropShape} actionDefinition the action definition of the filter that changed
   * @param {Array.<Object>} filters the next set of filters
   */
  onFiltersChange?: Function;

  /**
   * Whether items in this list can be reordered.
   * If true, then the caller **must** provide a onDragEnd responder.
   */
  orderable?: boolean;

  /**
   * Optional render function for customizing the column rendering.
   *
   * @param {ColumnPropTypes} columnData the column data for the column component
   */
  renderColumn?: Function;

  /**
   * Renders the given grid action.
   *
   * @param actionData the action
   * @return the rendered output for the action
   */
  renderGridAction?: Function;

  /**
   * Optional render function for customizing the header column rendering.
   *
   * @param {ColumnHeaderPropTypes} headerData the header data for the column header component
   */
  renderHeaderColumn?: Function;

  /**
   * Renders the given row action.
   *
   * @param actionData                   the action data
   * @param {ColumnPropTypes} columnData the column data
   * @return the rendered output for the action
   */
  renderRowAction?: Function;

  /**
   * Renders the given row message.
   *
   * @param messageData                   the message data
   * @param {ColumnPropTypes} columnData the column data
   * @return the rendered output for the message
   */
  renderRowMessage?: Function;

  /** the label that should be rendered for the row action header */
  rowActionHeaderLabel?: string | ReactNode | Element;

  /**
   * Optional function for customizing the key used for each row. This should be a
   * value unique to each row.
   *
   * @param row the row value
   * @param headers the headers for this row
   * @param index the row's index
   */
  rowKey?: string | Function | object;

  /** the rows for this list grid */
  rows?: Array<unknown>;
  rowComponent?: ReactNode;
  rowHeaderComponent?: ReactNode;
  rowMessages?: object | IMetadata;
  rowProps?: object;
  className?: string;
  /** @since 1.10.12 */
  wrapperClassName?: string;
  required?: boolean;
  renderDecorations?: Function;
  renderFooterButton?: Function;
  isSelectVisible?: boolean;
  isSelectAll?: boolean;
  isSelectNonVisible?: boolean;
  onClearAllSelections?: Function;
  onSelectVisible?: (checked: boolean) => void;
  onSelectAll?: Function;
  onSelectRow?: Function;
  onSortChange?: Function;
  paginateProps?: {
    disabled?: boolean;
    nextLabel?: string | ReactNode | Element;
    onPageChange?: Function;
    pageFirst?: boolean;
    pageLast?: boolean;
    prevLabel?: string | ReactNode | Element;
  };
  renderTableWrapper?: Function;
  selectable?: boolean;
  selectableType?: ICommonSelectableTypeIS;
  selectedCount?: number;
  selectedIds?: Array<string>;
  sorts?: any;
  sortable?: boolean;
  /** @since 1.10.12 */
  tiled?: boolean;
  border?: boolean;
  useFacetedSearch?: boolean;
}

AuroraActionListGrid.defaultProps = {
  actionDefinitions: [],
  error: [],
  emptyMessage: <FormattedMessage {...messages.emptyMessage} />,
  useFacetedSearch: false,
  rowActionHeaderLabel: ''
};
